import ChatInput from "./ChatInput";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ChatMessage from "./ChatMessage";
import { askGptByText } from "../../utils/gpt";
import RecordRTC, { Options, StereoAudioRecorder } from "recordrtc";
import { blobToFile, getVideoPath } from "../../utils/util";
import { speachToTextByWav } from "../../utils/stt";
import { createAvatarJob, getAvatar } from "../../utils/avatar";
import { JobStatusEnum } from "../../types/avatar";
import {
  LoadingMessageEnum,
  MessageTypeEnum,
  RoleEnum,
} from "../../types/chat";
import { useVideoContext, VideoStatusEnum } from "../../stores/video";
import { THistoryItem, useChatContext } from "../../stores/chat";
import { warmups } from "../../configs/warmups";
import { useWaveSurferRecorderContext } from "../../stores/wavesurfer-recorder";
import { Box, IconButton, Typography } from "@mui/material";
import { DeleteOutlined } from "@mui/icons-material";
import { deleteFileByPath, postUploadFile } from "../../utils/file";

type TProps = {
  onGetAvatar: (src: string) => void;
  className?: string;
  useAvatar: boolean;
};

export const ChatRoom = (props: TProps) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<RecordRTC | null>(null);

  const { recorder } = useWaveSurferRecorderContext();

  const [message, setMessage] = useState("");
  const isComposition = useRef(false);
  const [warmup, setWarmup] = useState(0);
  const [file, setFile] = useState<string | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const {
    status: playerRefStatus,
    play,
    setPlayerTimer,
    playerTimer,
  } = useVideoContext();
  const {
    isLoadingAvatar,
    setIsLoadingAvatar,
    history,
    setHistory,
    isLoadingStt,
    isLoadingGPT,
    isRecording,
    setIsRecording,
    setIsLoadingGPT,
    setIsLoadingStt,
  } = useChatContext();

  const handleSend = () => {
    if (!message) return;
    setHistory((prev: THistoryItem[]) => [
      ...prev,
      {
        role: RoleEnum.human,
        message,
        messageType: MessageTypeEnum.text,
      },
    ]);
    setMessage("");
  };

  const sendHumanMessage = useCallback(
    async (message: string) => {
      setHistory((pre) => [
        ...pre.filter(
          ({ messageType }) => messageType !== MessageTypeEnum.loading
        ),
        {
          role: RoleEnum.ai,
          message: LoadingMessageEnum.video,
          messageType: MessageTypeEnum.loading,
        },
      ]);

      // add warmup
      if (props.useAvatar) {
        play && play(getVideoPath(warmups[warmup].filename));
        setWarmup((pre) => {
          if (pre >= warmups.length - 1) return 0;
          return pre + 1;
        });
      }

      const RECORD_FOR_DEMO = false;

      if (RECORD_FOR_DEMO) {
        const jobId = "97cf93ab-d24f-42fa-9359-f051a389fad8";
        const res = await getAvatar(jobId);

        setTimeout(async () => {
          setHistory((pre) => [
            ...pre.filter(
              ({ messageType }) => messageType !== MessageTypeEnum.loading
            ),
            {
              role: RoleEnum.ai,
              message:
                "遠傳幣可用於「friDay購物」、「遠傳心生活」折抵、兌換商品，還可以折抵電信帳單、手機專案價、門市消費，使用超便利。",
              messageType: MessageTypeEnum.text,
            },
          ]);

          props.onGetAvatar(res.outputs.result);

          setHistory((pre) => [
            ...pre.filter(
              ({ messageType }) => messageType !== MessageTypeEnum.loading
            ),
            {
              role: RoleEnum.ai,
              message: jobId,
              messageType: MessageTypeEnum.video,
            },
          ]);
        }, 5000);
      } else {
        setIsLoadingGPT(true);
        const res = await askGptByText(
          message,
          file || "",
          props.useAvatar ? 50 : NaN
        );
        setIsLoadingGPT(false);
        const aiMessage = res ? res?.message : "";
        if (aiMessage && props.useAvatar) {
          // create Avatar Job
          setIsLoadingAvatar && setIsLoadingAvatar(true);
          const avatarJob = await createAvatarJob(aiMessage);
          const jobId = avatarJob.id;
          let status = avatarJob.status;
          let res: any;

          // GET Job
          while (
            status !== JobStatusEnum.Succeeded &&
            status !== JobStatusEnum.Failed
          ) {
            const job = await getAvatar(jobId);
            status = job["status"];
            console.log("getting avatar job", jobId, status);
            res = job;
          }

          if (status === JobStatusEnum.Failed) {
            alert("job failed");
          } else {
            props.onGetAvatar(res.outputs.result);
          }

          setIsLoadingAvatar && setIsLoadingAvatar(false);

          setHistory((pre) => [
            ...pre.filter(
              ({ messageType }) => messageType !== MessageTypeEnum.loading
            ),
            {
              role: RoleEnum.ai,
              message: aiMessage,
              messageType: MessageTypeEnum.text,
            },
          ]);

          setHistory((pre) => [
            ...pre.filter(
              ({ messageType }) => messageType !== MessageTypeEnum.loading
            ),
            {
              role: RoleEnum.ai,
              message: res.id,
              messageType: MessageTypeEnum.video,
            },
          ]);
        } else {
          setHistory((pre) => [
            ...pre.filter(
              ({ messageType }) => messageType !== MessageTypeEnum.loading
            ),
            {
              role: RoleEnum.ai,
              message: aiMessage,
              messageType: MessageTypeEnum.text,
              links: res.doc_url,
            },
          ]);
        }
      }
    },
    [play, props, setHistory, setIsLoadingAvatar, setIsLoadingGPT, warmup]
  );

  const startRecording = async () => {
    setIsRecording(true);
    streamRef.current = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: true,
    });

    const options: Options = {
      type: "audio",
      recorderType: StereoAudioRecorder,
      numberOfAudioChannels: 2,
      checkForInactiveTracks: true,
      bufferSize: 16384,
    };

    recorderRef.current = new RecordRTC(streamRef.current, options);
    recorderRef.current.startRecording();
    recorder?.startRecording();
  };

  const stopRecording = async () => {
    setIsRecording(false);
    recorderRef.current?.stopRecording(async () => {
      const blob = recorderRef.current?.getBlob();
      const file = blobToFile(blob!);

      recorderRef.current?.destroy();
      streamRef.current?.getAudioTracks()[0].stop();

      setHistory((prev) => [
        ...prev,
        {
          role: RoleEnum.human,
          message: LoadingMessageEnum.stt,
          messageType: MessageTypeEnum.loading,
        },
      ]);

      if (recorder?.isRecording()) {
        recorder?.stopRecording();
      }

      setIsLoadingStt(true);
      const text = await speachToTextByWav(file);
      setIsLoadingStt(false);
      if (!text) return;

      setHistory((prev) => [
        ...prev.filter(
          ({ messageType }) => messageType !== MessageTypeEnum.loading
        ),
        {
          role: RoleEnum.human,
          message: text.message,
          messageType: text.isError
            ? MessageTypeEnum.error
            : MessageTypeEnum.text,
        },
      ]);
    });
  };

  const handleClickRecord = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" || isComposition.current) return;
    handleSend();
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value.replaceAll(/\n/g, ""));
  };

  const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const tempFile = e.target.files?.[0];
    if (!tempFile) return;

    // check file extention
    const validExts = [".pdf", ".csv"];
    const fileExt = tempFile.name.substring(tempFile.name.lastIndexOf("."));
    if (validExts.indexOf(fileExt) < 0)
      alert("檔案類型錯誤，可接受的副檔名有：" + validExts.join("、"));

    setIsUploadingFile(true);
    try {
      if (file) {
        deleteFileByPath(file);
      }
      const res = await postUploadFile(tempFile);
      setFile(res.path as string);
    } catch (err) {
      console.warn(err);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleDeleteFile = async (file: string) => {
    if (isUploadingFile || isDeletingFile) return;
    setIsDeletingFile(true);
    try {
      await deleteFileByPath(file);
      setFile(null);
    } catch (err) {
      console.warn(err);
    } finally {
      setIsDeletingFile(false);
    }
  };

  useEffect(() => {
    const lastMessage = history[history.length - 1];
    if (history.length === 0) return;

    if (
      lastMessage.role === RoleEnum.human &&
      lastMessage.messageType !== MessageTypeEnum.error &&
      lastMessage.messageType !== MessageTypeEnum.loading
    ) {
      sendHumanMessage(lastMessage.message);
    }
  }, [history, sendHumanMessage]);

  useEffect(() => {
    // if playing any video clear timer
    if (playerTimer && playerRefStatus === VideoStatusEnum.play) {
      clearTimeout(playerTimer);
      setPlayerTimer(null);
    }

    // add warm up video
    if (
      (isLoadingAvatar || isLoadingGPT) &&
      playerRefStatus === VideoStatusEnum.ended &&
      !playerTimer &&
      props.useAvatar
    ) {
      setPlayerTimer(
        setTimeout(() => {
          play && play(getVideoPath(warmups[warmup].filename));
          setWarmup((pre) => {
            if (pre >= warmups.length - 1) return 0;
            return pre + 1;
          });
        }, 6000)
      );
    }
  }, [
    isLoadingAvatar,
    playerRefStatus,
    playerTimer,
    isLoadingGPT,
    play,
    setPlayerTimer,
    warmup,
    props.useAvatar,
  ]);

  useEffect(() => {
    if (
      typeof scrollerRef.current?.clientHeight !== "number" ||
      typeof scrollerRef.current?.scrollHeight !== "number"
    )
      return;

    if (
      scrollerRef.current.offsetHeight < scrollerRef.current.scrollHeight &&
      scrollerRef.current.scrollTop + scrollerRef.current.offsetHeight + 800 >
        scrollerRef.current.scrollHeight
    ) {
      scrollerRef.current?.scrollTo({
        top: scrollerRef.current?.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [scrollerRef, history]);

  return (
    <div className={`Chatroom w-full h-full ${props.className}`}>
      <div className="flex flex-col w-full h-full">
        <div className="flex justify-center pb-4">
          <img
            src={process.env.PUBLIC_URL + "/logo.svg"}
            alt="logo"
            className="max-w-[200px] z-10"
          />
        </div>
        <div
          ref={scrollerRef}
          id="scroller"
          className="w-full flex-grow max-h-full overflow-auto"
        >
          <div className="flex flex-col px-4">
            {history.map((item, index) => (
              <ChatMessage
                key={item.role + item.message + index}
                role={item.role}
                message={item.message}
                messageType={item.messageType}
                hideIcon={
                  item.role === RoleEnum.ai &&
                  history[index - 1].role === RoleEnum.ai
                }
                links={item.links}
              />
            ))}
          </div>
        </div>
        <div className="pt-6 w-full">
          <Box className="ml-4 mr-5 h-[26px] flex space-x-2">
            {isUploadingFile && (
              <Typography className="truncate text-gray-400 w-full">
                檔案上傳中...
              </Typography>
            )}
            {file && !isUploadingFile && (
              <>
                <Typography className="truncate text-gray-400 w-full">
                  {file.substring(file.lastIndexOf("/") + 1)}
                </Typography>
                <IconButton size="small" onClick={() => handleDeleteFile(file)}>
                  <DeleteOutlined className="text-gray-500" />
                </IconButton>
              </>
            )}
          </Box>
          <ChatInput
            onClickRecord={handleClickRecord}
            onClickSend={handleSend}
            onChange={handleChange}
            onComposition={(e) =>
              (isComposition.current = e.type !== "compositionend")
            }
            onChangeFile={handleChangeFile}
            onKeyDown={handleKeyDown}
            isRecording={isRecording}
            value={message}
            disabled={isLoadingGPT || isLoadingAvatar || isLoadingStt}
            uploadDisabled={isUploadingFile || isDeletingFile}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatRoom;
